import React, { useContext, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Accordion,
  Card,
  Button,
  Spinner,
  Form,
  Row,
  Col,
  OverlayTrigger,
  Tooltip,
} from 'react-bootstrap';
import { ArrowDownShort, ArrowLeftShort, ExclamationTriangle } from 'react-bootstrap-icons';
// import "./index.css";

import Help from 'components/Help';
import { LeadContext } from 'Leads/Details/hooks/context';
import { computeUtilityTotalUsageKwh } from 'Leads/Details/utils/utility';
import {
  getInverterGroup,
  isMicroInverterType,
  matchesPrimaryInverterType,
} from 'Leads/Details/utils/inverter';
import RoofSegments from './RoofSegments';
import Obstacles from './Obstacles';
import Trees from './Trees';
import Shading, { SystemLosses } from './Shading';
import Setback from './Setback';
import Design from './Design';
import useNumberDraft from './useNumberDraft';
import Revise from './Revise';
import Annotation from './Annotation';
import {
  segmentPropType, obstaclePropType, treePropType, panelsPropType,
} from '../props';
import GroundMount from './GroundMount';
import { FloatSelect, FloatInput } from 'components/Form/Inputs';
import Inverter from 'components/Hardware/Inverter';

function Menu({
  activeKey,
  designType,
  shaded,
  buffer,
  rowSpacing,
  panelTilt,
  azimuthRestriction,
  fireSetbacks,
  setbackMode,
  shadingIsRunning,
  serverSideShadingIsRunning,
  setbackModeHandler,
  fireSetbackHandler,
  colorRange,
  currentAccordionHandler,
  currentTabHandler,
  tool,
  toolHandler,
  selectedLabel,
  selectedPanels,
  selectedRoof,
  selectedLabelHandler,
  clearLabelsHandler,
  deleteShapeHandler,
  addPointToPolygonHandler,
  removePointFromPolygonHandler,
  newPanelHandler,
  deletePanelHandler,
  rotatePanelHandler,
  setPVwattsLosses,
  losses,
  segments,
  resolution,
  segmentAzimuthHandler,
  segmentPitchAndAreaHandler,
  segmentEaveHeightHandler,
  treeUpdateHandler,
  pitchInDegrees,
  onPitchInDegreesChange,
  obstacles,
  obstacleHeightHandler,
  obstacleRadiusHandler,
  obstacleSetbackHandler,
  obstacleOrientationHandler,
  trees,
  panels,
  placementHandler,
  shadingHandler,
  weatherDataset,
  setWeatherDataset,
  weatherDatasetFetchedPath,
  weatherDatasetReady,
  onWeatherDatasetFetched,
  measureHandler,
  runOptimizationHandler,
  runEdgeDetectionHandler,
  proposalHandler,
  isCreatingProposal,
  shadingNeedsRerun,
  shadingHasRun,
  panelAzimuthHandler,
  currentPanel,
  appliedPanel,
  currentInverter,
  currentInverterQuantity,
  currentAdditionalInverters,
  // Original Installation Package additional inverters, normalized to
  // {id, autoQuantity}. Used to lazily prefill rows when the user clicks
  // "+ Add" so the first row mirrors the first saved entry, the second row
  // the second saved entry, and so on. Empty when the package has no
  // additional inverters configured.
  packageAdditionalInverters,
  changePanelHardware,
  changeInverterHardware,
  changeInverterQuantity,
  changeAdditionalInverters,
  bufferHandler,
  rowSpacingHandler,
  panelTiltHandler,
  azimuthRestrictionHandler,
  showOverlay,
  toggleOverlay,
  shadingMonth,
  shadingStatus,
  designFrame,
  runAIHandler,
  hasDSM,
  roofAreasSqFt,
  roofAreasUnit,
  solarPanels,
  inverters,
  panelPlacementFillMode,
  setPanelPlacementFillMode,
  panelPlacementLayoutMode,
  setPanelPlacementLayoutMode,
  panelPlacementOnlySelectedRoof,
  setPanelPlacementOnlySelectedRoof,
  clearPanels,
  hasAutoCalculatedInverterQuantity,
  // Production props
  productionIsRunning,
  productionHandled,
  totalAnnualKwh,
  totalClippingKwh,
  dcAcRatio,
  stringingData,
  invertersUsed,
  productionPerPanel,
  selectedPanelCount,
  systemSizeKw,
  mpptCountTotal,
  productionHardware,
  handleSolarProduction,
  // Target production props
  useTargetProduction,
  targetProductionKwh,
  maxDcAcRatio,
  onMaxDcAcRatioChange,
  onlySelectedPanels,
  onOnlySelectedPanelsChange,
  onUseTargetProductionChange,
  onTargetProductionKwhChange,
  // Auto quantity props
  primaryInverterAutoQuantity,
  onPrimaryInverterAutoQuantityChange,
}) {
  const { lead } = useContext(LeadContext);

  // Inverter selection handlers for Production menu
  const weatherDatasetTooltipText =
    'Please fetch the Weather dataset before continuing.';

  const utilityUsageKwh = computeUtilityTotalUsageKwh(lead?.utility);

  const lastValidTargetProductionRef = useRef(null);
  const userClearedTargetProductionRef = useRef(false);

  useEffect(() => {
    const current = Number(targetProductionKwh);
    if (Number.isFinite(current) && current > 0) {
      lastValidTargetProductionRef.current = current;
      userClearedTargetProductionRef.current = false;
    }
  }, [targetProductionKwh]);

  useEffect(() => {
    if (!useTargetProduction) return;
    if (typeof onTargetProductionKwhChange !== 'function') return;
    const current = Number(targetProductionKwh);
    if (Number.isFinite(current) && current > 0) return;
    const cached = Number(lastValidTargetProductionRef.current);
    if (
      !userClearedTargetProductionRef.current &&
      Number.isFinite(cached) &&
      cached > 0
    ) {
      onTargetProductionKwhChange(cached);
      return;
    }
    if (!Number.isFinite(Number(utilityUsageKwh)) || Number(utilityUsageKwh) <= 0) return;
    onTargetProductionKwhChange(Math.round(Number(utilityUsageKwh)));
  }, [
    useTargetProduction,
    targetProductionKwh,
    utilityUsageKwh,
    onTargetProductionKwhChange,
  ]);

  // Draft binding for the Target Production input. Without this the
  // input was effectively un-erasable: backspacing to empty commits 0
  // to the parent, which trips the auto-fill useEffect above and
  // immediately re-populates the field with cached/utility usage. The
  // draft keeps the displayed value at '' while the user is focused,
  // even as parent state churns underneath, so they can clear and
  // retype freely. On blur (or once the user types a finite number)
  // the draft re-syncs to the actual committed value.
  //
  // skipEmptyCommit: false because empty *is* a meaningful action here
  // — it flips userClearedTargetProductionRef so downstream logic
  // (e.g. proposal generation) knows the user explicitly cleared the
  // field rather than just not yet entering one.
  const targetProductionInput = useNumberDraft(
    targetProductionKwh === 0 ? '' : targetProductionKwh,
    (val) => {
      if (val === undefined) {
        userClearedTargetProductionRef.current = true;
        lastValidTargetProductionRef.current = null;
        onTargetProductionKwhChange(0);
        return;
      }
      if (val < 0) return;
      if (val > 0) {
        userClearedTargetProductionRef.current = false;
        lastValidTargetProductionRef.current = val;
      } else {
        userClearedTargetProductionRef.current = true;
        lastValidTargetProductionRef.current = null;
      }
      onTargetProductionKwhChange(val);
    },
    { skipEmptyCommit: false }
  );

  // Max DC/AC Ratio: same coerce-to-default-on-empty bug, but the
  // parent has no "explicit empty" semantic — empty is just mid-edit.
  // skipEmptyCommit: true keeps the parent's stored ratio intact while
  // the user is clearing and retyping; blur snaps the draft back to
  // the parent value (default 1.2) if they didn't enter a number.
  const maxDcAcRatioInput = useNumberDraft(
    maxDcAcRatio ?? 1.2,
    (val) => {
      if (Number.isFinite(val)) onMaxDcAcRatioChange(val);
    }
  );

  const inverterOptions = inverters
    ? inverters
      .filter((inv) => {
        const additionalInverterIds = (currentAdditionalInverters || []).map(ai => ai.id).filter(Boolean);
        return !additionalInverterIds.includes(inv.id);
      })
      .map((inv) => ({
        id: inv.id,
        label: `${inv.manufacturer?.name ?? 'Undefined'}-${inv.model}`,
      }))
    : [];

  const handleInverterChoice = (e) => {
    const { value } = e.target;
    const inverter = inverters.find(({ id }) => id === value);
    changeInverterHardware(inverter);
  };

  const handleInverterQuantityChange = (e) => {
    const quantity = parseInt(e.target.value) || 1;
    changeInverterQuantity(quantity);
  };

  const handleAddAdditionalInverter = () => {
    const existing = currentAdditionalInverters || [];
    const presets = Array.isArray(packageAdditionalInverters)
      ? packageAdditionalInverters
      : [];
    // Positional preset lookup: the Nth additional row (0-indexed by
    // current row count) gets prefilled from the Nth saved entry on the
    // Installation Package. Once the saved list is exhausted, fall back
    // to an empty row so the user can manually pick.
    const presetForNextRow = presets[existing.length];
    const newInverter =
      presetForNextRow && presetForNextRow.id != null
        ? { id: presetForNextRow.id, autoQuantity: true }
        : { id: '', autoQuantity: true };
    changeAdditionalInverters([...existing, newInverter]);
  };

  const handleRemoveAdditionalInverter = (index) => {
    const updated = (currentAdditionalInverters || []).filter((_, i) => i !== index);
    changeAdditionalInverters(updated);
  };

  const handleAdditionalInverterChange = (index, field, value) => {
    const updated = (currentAdditionalInverters || []).map((inverter, i) =>
      i === index ? { ...inverter, [field]: value } : inverter
    );
    changeAdditionalInverters(updated);
  };

  // Check if current inverter is micro
  const isMicroInverter = getInverterGroup(currentInverter) === 'micro';
  const placedPanelCount = Array.isArray(panels)
    ? panels.reduce((total, segment) => {
        const horizontalCount = segment?.horizontal?.points?.length || 0;
        const verticalCount = segment?.vertical?.points?.length || 0;
        return total + horizontalCount + verticalCount;
      }, 0)
    : 0;
  const hasPlacedPanels = placedPanelCount > 0;
  const currentDesignPanel = hasPlacedPanels ? appliedPanel : currentPanel;
  const hasValidProduction =
    productionHandled &&
    productionHardware?.panel &&
    selectedPanelCount > 0 &&
    placedPanelCount === selectedPanelCount;
  const displayedProductionPanel =
    hasValidProduction
      ? productionHardware.panel
      : currentDesignPanel;
  const displayedProductionInverter =
    productionHandled && productionHardware?.inverter
      ? productionHardware.inverter
      : currentInverter;
  const displayedProductionAdditionalInverters =
    productionHandled && Array.isArray(productionHardware?.additionalInverters)
      ? productionHardware.additionalInverters
      : currentAdditionalInverters;
  const displayedProductionInverterQuantity =
    productionHandled && productionHardware?.inverterQuantity
      ? productionHardware.inverterQuantity
      : currentInverterQuantity;

  const getUsedCountForInverter = (inv) => {
    if (!productionHandled) return null;
    if (!inv) return null;
    if (!Array.isArray(invertersUsed) || invertersUsed.length === 0) return null;

    const invId = inv?.id;
    const invModel = inv?.model;

    if (invId !== undefined && invId !== null) {
      const match = invertersUsed.find(
        (x) => String(x?.hardware_id ?? x?.hardwareId ?? x?.id) === String(invId)
      );
      const count = Number(match?.count);
      return Number.isFinite(count) ? count : 0;
    }

    if (invModel) {
      const match = invertersUsed.find(
        (x) => String(x?.model || '').trim() === String(invModel).trim()
      );
      const count = Number(match?.count);
      return Number.isFinite(count) ? count : 0;
    }

    return null;
  };

  // True when there are no actually-placed panels on any roof segment.
  //
  // We can't rely on `panels.length === 0` because some code paths (e.g.
  // canvas3DElement.clearPanels) leave `panels` as an array of empty
  // placeholder structures (one per segment) with no `points` filled in.
  // That array has length > 0 even though there's nothing on the roof.
  const noPanelsPlaced =
    !Array.isArray(panels) ||
    panels.length === 0 ||
    !panels.some(
      (p) =>
        (p?.horizontal?.points?.length || 0) > 0 ||
        (p?.vertical?.points?.length || 0) > 0
    );

  // Helper function to get the reason why Calculate Production button is disabled.
  // Order matters — show the most fundamental missing prerequisite first so
  // the user knows the real next step (e.g. when both panels and shading are
  // missing, "place panels" is the actionable message, not "run shading").
  const getProductionDisabledReason = () => {
    if (productionIsRunning) return null; // Don't show message when calculating
    if (noPanelsPlaced) return 'Panels must be placed on roof segments under Design';
    if (!currentPanel) return 'Solar panel must be selected';
    if (!currentInverter) return 'Inverter must be selected';
    if (!shadingHasRun) return 'Run Shading before calculating production';
    if (shadingNeedsRerun) return 'Please re-run shading before calculating production';
    if (!weatherDatasetReady) return 'Weather dataset is required';
    return null; // Button should be enabled
  };

  const roofsHelp = 'Draw a polygon for each roof segment, and add an eave label to determine azimuth.';
  const obstaclesHelp = 'Select an obstacle shape and draw all roof obstacles.';
  const treesHelp = 'Draw a tree by drawing a circle outward from its center, and selecting a tree type.';
  const shadingHelp = 'Run annual shading analysis for annotated roof segments, and review roof insolation.';
  const setbackHelp = 'Configure fire setbacks.';
  const designHelp = 'Perform panel fit over roof segments based on selected solar panels and fire setback.';
  const reviseHelp = 'Add/remove and adjust alignment of solar panels for roof segments.';
  const annotationsHelp =
    'Place quick markers, draw driveways, and measure distances between points.';

  return (
    <div className='menu-layout'>
      <Accordion activeKey={activeKey} onSelect={currentAccordionHandler}>
        <Card className="shadow border rounded">
          <Accordion.Toggle
            className="bg-white text-bold h5 border-0 rounded"
            as={Card.Header}
            eventKey="segments"
          >
            Roof Segments
            {activeKey === 'segments' ? <ArrowLeftShort style={{ float: 'right' }} /> : <ArrowDownShort style={{ float: 'right' }} />}
            <Help text={roofsHelp} />
          </Accordion.Toggle>
          <Accordion.Collapse eventKey="segments">
            <Card.Body className="pt-0">
              <RoofSegments
                designType={designType}
                hasDSM={hasDSM}
                segments={segments}
                resolution={resolution}
                roofAreasSqFt={roofAreasSqFt}
                roofAreasUnit={roofAreasUnit}
                pitchInDegrees={pitchInDegrees}
                onPitchInDegreesChange={onPitchInDegreesChange}
                segmentAzimuthHandler={segmentAzimuthHandler}
                segmentPitchAndAreaHandler={segmentPitchAndAreaHandler}
                segmentEaveHeightHandler={segmentEaveHeightHandler}
                currentTabHandler={currentTabHandler}
                selectedLabel={selectedLabel}
                selectedLabelHandler={selectedLabelHandler}
                clearLabelsHandler={clearLabelsHandler}
                deleteShapeHandler={deleteShapeHandler}
                addPointToPolygonHandler={addPointToPolygonHandler}
                removePointFromPolygonHandler={removePointFromPolygonHandler}
                measureHandler={measureHandler}
                runOptimizationHandler={runOptimizationHandler}
                runEdgeDetectionHandler={runEdgeDetectionHandler}
                runAIHandler={runAIHandler}
              />
            </Card.Body>
          </Accordion.Collapse>
        </Card>

        <Card className="shadow border rounded">
          <Accordion.Toggle
            className="bg-white text-bold h5 border-0 rounded"
            as={Card.Header}
            eventKey="obstacles"
          >
            Obstacles
            {activeKey === 'obstacles' ? <ArrowLeftShort style={{ float: 'right' }} /> : <ArrowDownShort style={{ float: 'right' }} />}
            <Help text={obstaclesHelp} />
          </Accordion.Toggle>
          <Accordion.Collapse eventKey="obstacles">
            <Card.Body className="pt-0">
              <Obstacles
                obstacles={obstacles}
                tool={tool}
                toolHandler={toolHandler}
                obstacleRadiusHandler={obstacleRadiusHandler}
                obstacleHeightHandler={obstacleHeightHandler}
                obstacleSetbackHandler={obstacleSetbackHandler}
                obstacleOrientationHandler={obstacleOrientationHandler}
                deleteShapeHandler={deleteShapeHandler}
              />
            </Card.Body>
          </Accordion.Collapse>
        </Card>

        <Card className="shadow border rounded">
          <Accordion.Toggle
            className="bg-white text-bold h5 border-0 rounded"
            as={Card.Header}
            eventKey="trees"
          >
            Trees
            {activeKey === 'trees' ? <ArrowLeftShort style={{ float: 'right' }} /> : <ArrowDownShort style={{ float: 'right' }} />}
            <Help text={treesHelp} />
          </Accordion.Toggle>
          <Accordion.Collapse eventKey="trees">
            <Card.Body className="pt-0">
              <Trees
                trees={trees}
                treeUpdateHandler={treeUpdateHandler}
                deleteShapeHandler={deleteShapeHandler}
              />
            </Card.Body>
          </Accordion.Collapse>
        </Card>
        <Card className="shadow border rounded ">
          <Accordion.Toggle
            className="bg-white text-bold h5 border-0 rounded"
            as={Card.Header}
            eventKey="setback"
          >
            Fire Setbacks
            {activeKey === 'setback' ? <ArrowLeftShort style={{ float: 'right' }} /> : <ArrowDownShort style={{ float: 'right' }} />}
            <Help text={setbackHelp} />
          </Accordion.Toggle>
          <Accordion.Collapse eventKey="setback">
            <Card.Body className="pt-0">
              <Setback
                segments={segments}
                fireSetbacks={fireSetbacks}
                setbackMode={setbackMode}
                setbackModeHandler={setbackModeHandler}
                fireSetbackHandler={fireSetbackHandler}
              />
            </Card.Body>
          </Accordion.Collapse>
        </Card>

        <Card className="shadow border rounded ">
          <Accordion.Toggle
            className="bg-white text-bold h5 border-0 rounded"
            as={Card.Header}
            eventKey="design"
          >
            Design
            {activeKey === 'design' ? <ArrowLeftShort style={{ float: 'right' }} /> : <ArrowDownShort style={{ float: 'right' }} />}
            <Help text={designHelp} />
          </Accordion.Toggle>
          <Accordion.Collapse eventKey="design">
            <Card.Body className="pt-0">
              <Design
                currentPanel={currentPanel}
                buffer={buffer}
                rowSpacing={rowSpacing}
                panelTilt={panelTilt}
                azimuthRestriction={azimuthRestriction}
                changePanelHardware={changePanelHardware}
                placementHandler={placementHandler}
                bufferHandler={bufferHandler}
                rowSpacingHandler={rowSpacingHandler}
                panelTiltHandler={panelTiltHandler}
                azimuthRestrictionHandler={azimuthRestrictionHandler}
                solarPanels={solarPanels}
                panelPlacementFillMode={panelPlacementFillMode}
                setPanelPlacementFillMode={setPanelPlacementFillMode}
                panelPlacementLayoutMode={panelPlacementLayoutMode}
                setPanelPlacementLayoutMode={setPanelPlacementLayoutMode}
                panelPlacementOnlySelectedRoof={panelPlacementOnlySelectedRoof}
                setPanelPlacementOnlySelectedRoof={setPanelPlacementOnlySelectedRoof}
                clearPanels={clearPanels}
              />
            </Card.Body>
          </Accordion.Collapse>
        </Card>

        <Card className="shadow border rounded ">
          <Accordion.Toggle
            className="bg-white text-bold h5 border-0 rounded"
            as={Card.Header}
            eventKey="revise"
          >
            Edit Panel Placement
            {activeKey === 'revise' ? <ArrowLeftShort style={{ float: 'right' }} /> : <ArrowDownShort style={{ float: 'right' }} />}
            <Help text={reviseHelp} />
          </Accordion.Toggle>
          <Accordion.Collapse eventKey="revise">
            <Card.Body className="pt-0">
              <Revise
                currentPanel={currentDesignPanel}
                selectedPanels={selectedPanels}
                selectedRoof={selectedRoof}
                newPanelHandler={newPanelHandler}
                deletePanelHandler={deletePanelHandler}
                rotatePanelHandler={rotatePanelHandler}
                proposalHandler={proposalHandler}
                panels={panels}
                segments={segments}
                panelAzimuthHandler={panelAzimuthHandler}
                clearPanels={clearPanels}
                placementHandler={placementHandler}
                panelPlacementFillMode={panelPlacementFillMode}
                setPanelPlacementFillMode={setPanelPlacementFillMode}
                panelPlacementLayoutMode={panelPlacementLayoutMode}
                setPanelPlacementLayoutMode={setPanelPlacementLayoutMode}
              />
            </Card.Body>
          </Accordion.Collapse>
        </Card>

        {/* <Card className="shadow border rounded ">
          <Accordion.Toggle
            className="bg-white text-bold h5 border-0 rounded"
            as={Card.Header}
            eventKey="ground_mount"
          >
            Ground Mount
            { activeKey === 'ground_mount' ? <ArrowLeftShort style={{ float: 'right' }} /> : <ArrowDownShort style={{ float: 'right' }} /> }
            <Help text={designHelp} />
          </Accordion.Toggle>
          <Accordion.Collapse eventKey="ground_mount">
            <Card.Body className="pt-0">
              <GroundMount
                currentPanel={currentPanel}
                currentInverter={currentInverter}
                currentAdditionalInverters={currentAdditionalInverters}
                buffer={buffer}
                rowSpacing={rowSpacing}
                panelTilt={panelTilt}
                solarPanels={solarPanels}
                inverters={inverters}
                panelPlacementFillMode={panelPlacementFillMode}
                panelPlacementLayoutMode={panelPlacementLayoutMode}
                panelPlacementOnlySelectedRoof={panelPlacementOnlySelectedRoof}
              />
            </Card.Body>
          </Accordion.Collapse>
        </Card> */}

        <Card className="shadow border rounded ">
          <Accordion.Toggle
            className="bg-white text-bold h5 border-0 rounded"
            as={Card.Header}
            eventKey="shading"
          >
            Shading Analysis
            {shadingNeedsRerun && (
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip id="tooltip-shading-rerun">Rerun shading</Tooltip>}
              >
                <ExclamationTriangle className="text-danger ml-2" style={{ cursor: 'help' }} />
              </OverlayTrigger>
            )}
            {activeKey === 'shading' ? <ArrowLeftShort style={{ float: 'right' }} /> : <ArrowDownShort style={{ float: 'right' }} />}
            <Help text={shadingHelp} />
          </Accordion.Toggle>
          <Accordion.Collapse eventKey="shading">
            <Card.Body className="pt-0">
              <Shading
                shaded={shaded}
                colorRange={colorRange}
                shadingHandler={shadingHandler}
                weatherDataset={weatherDataset}
                setWeatherDataset={setWeatherDataset}
                weatherDatasetFetchedPath={weatherDatasetFetchedPath}
                weatherDatasetReady={weatherDatasetReady}
                onWeatherDatasetFetched={onWeatherDatasetFetched}
                showOverlay={showOverlay}
                toggleOverlay={toggleOverlay}
                shadingMonth={shadingMonth}
                shadingStatus={shadingStatus}
                shadingIsRunning={shadingIsRunning}
                serverSideShadingIsRunning={serverSideShadingIsRunning}
              />
            </Card.Body>
          </Accordion.Collapse>
        </Card>

        <Card className="shadow border rounded ">
          <Accordion.Toggle
            className="bg-white text-bold h5 border-0 rounded"
            as={Card.Header}
            eventKey="production"
          >
            Production
            {shadingHasRun && (!productionHandled || shadingNeedsRerun) && (
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip id="tooltip-production-rerun">Rerun calculate production</Tooltip>}
              >
                <ExclamationTriangle className="text-danger ml-2" style={{ cursor: 'help' }} />
              </OverlayTrigger>
            )}
            {activeKey === 'production' ? <ArrowLeftShort style={{ float: 'right' }} /> : <ArrowDownShort style={{ float: 'right' }} />}
            <Help text="Calculate solar production for all panels using the solar-production service." />
          </Accordion.Toggle>
          <Accordion.Collapse eventKey="production">
            <Card.Body className="pt-0">
              <div className="mb-3">
                <div className="small text-muted mb-2">
                  <div>
                    <strong>Weather Dataset:</strong>{' '}
                    {weatherDataset || '—'}
                  </div>
                  <div>
                    <strong>Utility Usage:</strong>{' '}
                    {Number.isFinite(utilityUsageKwh)
                      ? `${Math.round(utilityUsageKwh).toLocaleString()} kWh`
                      : '—'}
                  </div>
                </div>
                <h6 className="text-muted">System Losses</h6>
                <SystemLosses
                  setPVwattsLosses={setPVwattsLosses}
                  initialLosses={losses}
                />
              </div>

              {/* Inverter Selection Section */}
              <div className="mb-3">
                <h6 className="text-muted">Inverter Configuration</h6>
                <Row className="mb-2 align-items-end">
                  <Col sm={isMicroInverter ? "12" : "7"}>
                    <FloatSelect
                      label="Primary Inverter"
                      options={inverterOptions}
                      labelField="label"
                      value={currentInverter?.id || ''}
                      onChange={handleInverterChoice}
                      allowNull
                      nullValue="Not selected"
                    />
                  </Col>
                  {!isMicroInverter && (
                    <>
                      <Col sm="1" className="d-flex justify-content-center pb-2">
                        <Form.Check
                          type="checkbox"
                          checked={!primaryInverterAutoQuantity}
                          onChange={(e) => onPrimaryInverterAutoQuantityChange(!e.target.checked)}
                          title="Manual quantity"
                        />
                      </Col>
                      <Col sm="4">
                        <FloatInput
                          label="Quantity"
                          type={primaryInverterAutoQuantity ? "text" : "number"}
                          min="1"
                          max="10"
                          value={primaryInverterAutoQuantity ? "Auto" : currentInverterQuantity}
                          onChange={handleInverterQuantityChange}
                          disabled={primaryInverterAutoQuantity}
                        />
                      </Col>
                    </>
                  )}
                </Row>

                {/* Additional Inverters Section */}
                {!isMicroInverter && (
                  <>
                    <div className="d-flex justify-content-between align-items-center mb-2 mt-3">
                      <label className="form-label mb-0 small">
                        Additional Inverters
                      </label>
                      <Button
                        size="sm"
                        variant="outline-primary"
                        onClick={handleAddAdditionalInverter}
                      >
                        + Add
                      </Button>
                    </div>
                    {(currentAdditionalInverters || []).map(
                      (additionalInverter, index) => {
                        // Additional inverters are restricted to central
                        // (non-micro) regardless of the primary inverter type,
                        // mirroring the Installation Settings rule. We also
                        // exclude the primary and any other additional rows
                        // to prevent duplicates.
                        const availableOptions = inverters
                          ? inverters
                            .filter((inv) => {
                              const isCentral = !isMicroInverterType(inv?.type);
                              const isNotPrimary =
                                inv.id !== currentInverter?.id;
                              const isNotOtherAdditional =
                                !currentAdditionalInverters
                                  .filter((_, i) => i !== index)
                                  .map((ai) => ai.id)
                                  .includes(inv.id);
                              return (
                                isCentral &&
                                isNotPrimary &&
                                isNotOtherAdditional
                              );
                            })
                            .map((inv) => ({
                              id: inv.id,
                              label: `${inv.manufacturer?.name ?? 'Undefined'
                                }-${inv.model}`,
                            }))
                          : [];

                        const isAutoQty =
                          additionalInverter.autoQuantity !== false;

                        return (
                          <Row key={index} className="mb-2 align-items-end">
                            <Col sm="7">
                              <FloatSelect
                                label={`Inverter ${index + 2}`}
                                options={availableOptions}
                                labelField="label"
                                value={additionalInverter.id || ''}
                                onChange={(e) =>
                                  handleAdditionalInverterChange(
                                    index,
                                    'id',
                                    e.target.value
                                  )
                                }
                                allowNull
                                nullValue="Not selected"
                              />
                            </Col>
                            <Col
                              sm="1"
                              className="d-flex justify-content-center pb-2"
                            >
                              <Form.Check
                                type="checkbox"
                                checked={!isAutoQty}
                                onChange={(e) =>
                                  handleAdditionalInverterChange(
                                    index,
                                    'autoQuantity',
                                    !e.target.checked
                                  )
                                }
                                title="Manual quantity"
                              />
                            </Col>
                            <Col sm="4">
                              <FloatInput
                                label="Quantity"
                                type={isAutoQty ? 'text' : 'number'}
                                min="1"
                                value={
                                  isAutoQty
                                    ? 'Auto'
                                    : additionalInverter.quantity || 1
                                }
                                onChange={(e) =>
                                  handleAdditionalInverterChange(
                                    index,
                                    'quantity',
                                    parseInt(e.target.value) || 1
                                  )
                                }
                                disabled={isAutoQty}
                                append={
                                  <Button
                                    size="sm"
                                    variant="outline-danger"
                                    onClick={() =>
                                      handleRemoveAdditionalInverter(index)
                                    }
                                  >
                                    ×
                                  </Button>
                                }
                              />
                            </Col>
                          </Row>
                        );
                      }
                    )}
                  </>
                )}
              </div>

              {/* Hardware Info Section */}
              <div className="mb-3 border-top pt-3">
                <h6 className="text-muted">Selected Hardware</h6>
                <p className="mb-1"><strong>Panel:</strong> {displayedProductionPanel?.name || displayedProductionPanel?.model || 'Not selected'} {displayedProductionPanel?.power ? `(${displayedProductionPanel.power}W)` : ''}</p>
                <div className="mb-1">
                  <strong>Inverters:</strong>
                  {displayedProductionInverter ? (
                    <ul className="mb-0 ps-3" style={{ fontSize: '0.9em' }}>
                      <li>
                        {displayedProductionInverter.model} ({displayedProductionInverter.capacity}W) × {primaryInverterAutoQuantity ? 'Auto' : displayedProductionInverterQuantity}
                        {(() => {
                          const used = getUsedCountForInverter(displayedProductionInverter);
                          if (!Number.isFinite(used)) return null;
                          return (
                            <span className="text-danger ms-2">
                              &nbsp;(Used: {used})
                            </span>
                          );
                        })()}
                      </li>
                      {getInverterGroup(displayedProductionInverter) !== 'micro' &&
                        (displayedProductionAdditionalInverters || []).map((addInv, idx) => {
                          const invData = inverters?.find(
                            (i) => i.id === addInv.id
                          );
                          if (!invData) return null;
                          const isAutoQty = addInv.autoQuantity !== false;
                          return (
                            <li key={idx}>
                              {invData.model} ({invData.capacity}W) ×{' '}
                              {isAutoQty ? 'Auto' : addInv.quantity || 1}
                              {(() => {
                                const used = getUsedCountForInverter(invData);
                                if (!Number.isFinite(used)) return null;
                                return (
                                  <span className="text-danger ms-2">
                                    (Used: {used})
                                  </span>
                                );
                              })()}
                            </li>
                          );
                        })}
                    </ul>
                  ) : (
                    <span> Not selected</span>
                  )}
                </div>
              </div>

              <div className="mb-3 border-top pt-3">
                {(() => {
                  const productionModeOptions = [
                    { id: 'all-panels', label: 'All panels' },
                    {
                      id: 'target-all',
                      label: 'Use Target Production (Optimal Panel Selection)',
                    },
                    { id: 'selected-only', label: 'Only Selected Panels' },
                    {
                      id: 'target-selected',
                      label: 'Target Production (Only Selected Panels)',
                    },
                  ];

                  const mode =
                    useTargetProduction && onlySelectedPanels
                      ? 'target-selected'
                      : useTargetProduction
                      ? 'target-all'
                      : onlySelectedPanels
                      ? 'selected-only'
                      : 'all-panels';

                  const setMode = (next) => {
                    const nextMode = next?.target?.value ?? next;
                    const wantsTarget =
                      nextMode === 'target-all' || nextMode === 'target-selected';
                    const wantsSelectedOnly =
                      nextMode === 'selected-only' ||
                      nextMode === 'target-selected';

                    onUseTargetProductionChange(Boolean(wantsTarget));
                    onOnlySelectedPanelsChange(Boolean(wantsSelectedOnly));
                  };

                  return (
                    <div className="mb-2">
                      <FloatSelect
                        label="Production Mode"
                        options={productionModeOptions}
                        labelField="label"
                        value={mode}
                        onChange={setMode}
                        allowNull={false}
                      />
                    </div>
                  );
                })()}
                {useTargetProduction && (
                  <>
                    <FloatInput
                      label="Target Production"
                      type="text"
                      append="kWh"
                      className="mt-2"
                      {...targetProductionInput}
                    />
                  </>
                )}
                <FloatInput
                  label="Max DC/AC Ratio"
                  type="number"
                  min="1.0"
                  max="1.8"
                  step="0.01"
                  className="mt-2"
                  {...maxDcAcRatioInput}
                />
              </div>

              {/* Calculate Button */}
              {(() => {
                const isDisabled =
                  productionIsRunning ||
                  !shadingHasRun ||
                  shadingNeedsRerun ||
                  !weatherDatasetReady ||
                  !currentPanel ||
                  !currentInverter ||
                  noPanelsPlaced;

                // Determine tooltip text based on the primary reason for disable.
                // Use the same precedence as the inline notification helper so
                // the tooltip and the message under the button always agree.
                let tooltipText = getProductionDisabledReason();
                // Special-case the weather tooltip which has its own copy.
                if (
                  tooltipText === 'Weather dataset is required' &&
                  weatherDatasetTooltipText
                ) {
                  tooltipText = weatherDatasetTooltipText;
                }

                if (isDisabled && tooltipText) {
                  return (
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip id="tooltip-production-disabled">
                          {tooltipText}
                        </Tooltip>
                      }
                    >
                      <span
                        className="d-inline-block w-100 mb-3"
                        style={{ cursor: 'not-allowed' }}
                      >
                        <Button
                          variant="primary"
                          onClick={handleSolarProduction}
                          disabled
                          className="w-100"
                          style={{ pointerEvents: 'none' }}
                        >
                          {productionIsRunning ? (
                            <>
                              <Spinner
                                animation="border"
                                size="sm"
                                className="me-2"
                              />
                              Calculating...
                            </>
                          ) : (
                            'Calculate Production'
                          )}
                        </Button>
                      </span>
                    </OverlayTrigger>
                  );
                }

                return (
                  <Button
                    variant="primary"
                    onClick={handleSolarProduction}
                    disabled={isDisabled}
                    className="w-100 mb-1"
                  >
                    {productionIsRunning ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Calculating...
                      </>
                    ) : (
                      'Calculate Production'
                    )}
                  </Button>
                );
              })()}
              {(() => {
                const disabledReason = getProductionDisabledReason();
                if (disabledReason) {
                  return (
                    <div className="text-danger small mb-3" style={{ textAlign: 'center' }}>
                      {disabledReason}
                    </div>
                  );
                }
                return null;
              })()}

              {/* Production Results (shown after calculation) */}
              {productionHandled && (
                <div className="production-results border-top pt-3">
                  <h6 className="text-muted">Production Results</h6>
                  <p className="mb-1"><strong>Total Annual:</strong> {totalAnnualKwh?.toFixed(0) || 0} kWh</p>
                  <p className="mb-1">
                    <strong>System Size:</strong>{' '}
                    {systemSizeKw !== null && systemSizeKw !== undefined
                      ? `${Number(systemSizeKw).toFixed(3)} kW`
                      : '—'}
                  </p>
                  <p className="mb-1"><strong>Selected Panels:</strong> {selectedPanelCount || 0}</p>
                  <p className="mb-1"><strong>Per Panel:</strong> {productionPerPanel?.toFixed(0) || 0} kWh/panel</p>
                  <p className="mb-1"><strong>DC/AC Ratio:</strong> {dcAcRatio?.toFixed(2) || 'N/A'}</p>
                  <p className="mb-1"><strong>Annual Clipping:</strong> {totalClippingKwh?.toFixed(0) || 0} kWh</p>
                  <p className="mb-1">
                    <strong>Strings:</strong>{' '}
                    {Array.isArray(stringingData)
                      ? stringingData.filter((s) => s?.draw !== false).length
                      : 0}
                  </p>
                  <p className="mb-1"><strong>Inverters:</strong> {invertersUsed?.reduce((sum, i) => sum + (i.count || 0), 0) || 0}</p>
                  <p className="mb-1">
                    <strong>MPPTs:</strong>{' '}
                    {mpptCountTotal !== null && mpptCountTotal !== undefined
                      ? Number(mpptCountTotal)
                      : '—'}
                  </p>
                </div>
              )}

              {/* Inverter Details */}
              {currentInverter && (
                <div className="mt-3 border">
                  <Inverter inverter={currentInverter} />
                </div>
              )}
            </Card.Body>
          </Accordion.Collapse>
        </Card>

        <Card className="shadow border rounded ">
          <Accordion.Toggle
            className="bg-white text-bold h5 border-0 rounded"
            as={Card.Header}
            eventKey="annotations"
          >
            Annotations
            {activeKey === 'annotations' ? <ArrowLeftShort style={{ float: 'right' }} /> : <ArrowDownShort style={{ float: 'right' }} />}
            <Help text={annotationsHelp} />
          </Accordion.Toggle>
          <Accordion.Collapse eventKey="annotations">
            <Card.Body className="pt-0">
              <Annotation
              />
            </Card.Body>
          </Accordion.Collapse>
        </Card>
      </Accordion>
      {!designFrame && (
        (!weatherDatasetReady &&
          (panels.length === 0 || isCreatingProposal || !weatherDatasetReady || shadingNeedsRerun || !productionHandled)) ? (
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip id="tooltip-proposal-dataset">
                {weatherDatasetTooltipText}
              </Tooltip>
            }
          >
            <span
              className="d-inline-block mt-3"
              style={{ float: 'right', cursor: 'not-allowed' }}
            >
              <Button
                className="create-proposal"
                onClick={proposalHandler}
                disabled
                style={{ pointerEvents: 'none' }}
              >
                {isCreatingProposal ? (
                  <>
                    Creating...
                    <Spinner animation="border" size="sm" className="ml-2" />
                  </>
                ) : (
                  'Create Proposal'
                )}
              </Button>
            </span>
          </OverlayTrigger>
        ) : (
          shadingNeedsRerun &&
            panels.length > 0 &&
            !isCreatingProposal &&
            weatherDatasetReady &&
            productionHandled ? (
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip id="tooltip-proposal-shading">
                  Please re-run shading before creating a proposal
                </Tooltip>
              }
            >
              <span
                className="d-inline-block mt-3"
                style={{ float: 'right', cursor: 'not-allowed' }}
              >
                <Button
                  className="create-proposal"
                  onClick={proposalHandler}
                  disabled
                  style={{ pointerEvents: 'none' }}
                >
                  {isCreatingProposal ? (
                    <>
                      Creating...
                      <Spinner animation="border" size="sm" className="ml-2" />
                    </>
                  ) : (
                    'Create Proposal'
                  )}
                </Button>
              </span>
            </OverlayTrigger>
          ) : (
            !productionHandled &&
            panels.length > 0 &&
            !isCreatingProposal &&
            weatherDatasetReady &&
            !shadingNeedsRerun ? (
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip id="tooltip-proposal-production">
                  Please calculate production before creating a proposal
                </Tooltip>
              }
            >
              <span
                className="d-inline-block mt-3"
                style={{ float: 'right', cursor: 'not-allowed' }}
              >
                <Button
                  className="create-proposal"
                  onClick={proposalHandler}
                  disabled
                  style={{ pointerEvents: 'none' }}
                >
                  {isCreatingProposal ? (
                    <>
                      Creating...
                      <Spinner animation="border" size="sm" className="ml-2" />
                    </>
                  ) : (
                    'Create Proposal'
                  )}
                </Button>
              </span>
            </OverlayTrigger>
          ) : (
            <Button
              className="create-proposal mt-3"
              onClick={proposalHandler}
              disabled={
                panels.length === 0 ||
                isCreatingProposal ||
                !weatherDatasetReady ||
                shadingNeedsRerun ||
                !productionHandled
              }
              style={{ float: 'right' }}
            >
              {isCreatingProposal ? (
                <>
                  Creating...
                  <Spinner animation="border" size="sm" className="ml-2" />
                </>
              ) : (
                'Create Proposal'
              )}
            </Button>
          )
        )
      )
      )}
    </div>
  );
}

Menu.defaultProps = {
  setbackMode: 0,
  currentPanel: '',
  isCreatingProposal: false,
  shadingNeedsRerun: false,
  shadingHasRun: false,
};

Menu.propTypes = {
  activeKey: PropTypes.string.isRequired,
  designType: PropTypes.oneOf(['M', 'A']).isRequired,
  hasDSM: PropTypes.bool.isRequired,
  currentAccordionHandler: PropTypes.func.isRequired,
  proposalHandler: PropTypes.func.isRequired,
  isCreatingProposal: PropTypes.bool,
  shadingNeedsRerun: PropTypes.bool,
  shadingHasRun: PropTypes.bool,
  // Roof Segments
  segments: segmentPropType.isRequired,
  roofAreasSqFt: PropTypes.array,
  roofAreasUnit: PropTypes.string,
  selectedLabel: PropTypes.string.isRequired,
  currentTabHandler: PropTypes.func.isRequired,
  segmentAzimuthHandler: PropTypes.func.isRequired,
  segmentPitchAndAreaHandler: PropTypes.func.isRequired,
  segmentEaveHeightHandler: PropTypes.func.isRequired,
  deleteShapeHandler: PropTypes.func.isRequired,
  pitchInDegrees: PropTypes.bool,
  onPitchInDegreesChange: PropTypes.func.isRequired,
  selectedLabelHandler: PropTypes.func.isRequired,
  clearLabelsHandler: PropTypes.func.isRequired,
  addPointToPolygonHandler: PropTypes.func.isRequired,
  removePointFromPolygonHandler: PropTypes.func.isRequired,
  measureHandler: PropTypes.func.isRequired,
  runOptimizationHandler: PropTypes.func.isRequired,
  runAIHandler: PropTypes.func.isRequired,
  // Obstacles
  obstacles: obstaclePropType.isRequired,
  toolHandler: PropTypes.func.isRequired,
  obstacleRadiusHandler: obstaclePropType.isRequired,
  obstacleSetbackHandler: obstaclePropType.isRequired,
  obstacleOrientationHandler: obstaclePropType.isRequired,
  obstacleHeightHandler: obstaclePropType.isRequired,
  // Trees
  trees: treePropType.isRequired,
  treeUpdateHandler: PropTypes.func.isRequired,
  // Shading
  shaded: PropTypes.bool.isRequired,
  colorRange: PropTypes.arrayOf(PropTypes.number).isRequired,
  shadingHandler: PropTypes.func.isRequired,
  weatherDataset: PropTypes.string,
  setWeatherDataset: PropTypes.func,
  weatherDatasetFetchedPath: PropTypes.string,
  weatherDatasetReady: PropTypes.bool,
  onWeatherDatasetFetched: PropTypes.func,
  showOverlay: PropTypes.bool.isRequired,
  toggleOverlay: PropTypes.func.isRequired,
  shadingMonth: PropTypes.number.isRequired,
  shadingStatus: PropTypes.number.isRequired,
  shadingIsRunning: PropTypes.bool,
  serverSideShadingIsRunning: PropTypes.bool,
  // Fire Setbacks
  fireSetbacks: PropTypes.shape({
    general: PropTypes.number,
    eave: PropTypes.number,
    ridge: PropTypes.number,
    rake: PropTypes.number,
    hip: PropTypes.number,
    valley: PropTypes.number,
  }).isRequired,
  setbackMode: PropTypes.number,
  setbackModeHandler: PropTypes.func.isRequired,
  fireSetbackHandler: PropTypes.func.isRequired,
  // Design
  currentPanel: PropTypes.string,
  appliedPanel: PropTypes.object,
  buffer: PropTypes.number.isRequired,
  rowSpacing: PropTypes.number.isRequired,
  panelTilt: PropTypes.number.isRequired,
  azimuthRestriction: PropTypes.arrayOf(PropTypes.number),
  changePanelHardware: PropTypes.func.isRequired,
  placementHandler: PropTypes.func.isRequired,
  bufferHandler: PropTypes.func.isRequired,
  rowSpacingHandler: PropTypes.func.isRequired,
  panelTiltHandler: PropTypes.func.isRequired,
  azimuthRestrictionHandler: PropTypes.func,
  // Revise
  selectedRoof: PropTypes.number.isRequired,
  selectedPanels: PropTypes.bool.isRequired,
  panels: panelsPropType.isRequired,
  panelAzimuthHandler: PropTypes.func.isRequired,
  newPanelHandler: PropTypes.func.isRequired,
  rotatePanelHandler: PropTypes.func.isRequired,
  deletePanelHandler: PropTypes.func.isRequired,

  panelPlacementFillMode: PropTypes.number.isRequired,
  setPanelPlacementFillMode: PropTypes.func.isRequired,
  panelPlacementLayoutMode: PropTypes.number.isRequired,
  setPanelPlacementLayoutMode: PropTypes.func.isRequired,
  panelPlacementOnlySelectedRoof: PropTypes.bool.isRequired,
  setPanelPlacementOnlySelectedRoof: PropTypes.func.isRequired,
  clearPanels: PropTypes.func.isRequired,
  setPVwattsLosses: PropTypes.func,
  losses: PropTypes.object,
  // Production props
  productionIsRunning: PropTypes.bool,
  productionHandled: PropTypes.bool,
  totalAnnualKwh: PropTypes.number,
  totalClippingKwh: PropTypes.number,
  dcAcRatio: PropTypes.number,
  stringingData: PropTypes.array,
  invertersUsed: PropTypes.array,
  productionPerPanel: PropTypes.number,
  selectedPanelCount: PropTypes.number,
  productionHardware: PropTypes.object,
  systemSizeKw: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  mpptCountTotal: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  handleSolarProduction: PropTypes.func,
  // Target production props
  useTargetProduction: PropTypes.bool,
  targetProductionKwh: PropTypes.number,
  maxDcAcRatio: PropTypes.number,
  onMaxDcAcRatioChange: PropTypes.func,
  onlySelectedPanels: PropTypes.bool,
  onOnlySelectedPanelsChange: PropTypes.func,
  onUseTargetProductionChange: PropTypes.func,
  onTargetProductionKwhChange: PropTypes.func,
  // Auto quantity props
  primaryInverterAutoQuantity: PropTypes.bool,
  onPrimaryInverterAutoQuantityChange: PropTypes.func,
  packageAdditionalInverters: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      autoQuantity: PropTypes.bool,
    })
  ),
};

export default Menu;
